import { useCallback, useEffect } from 'react';

import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';

export const getParentPath = (pathname: string): string | null => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/' || path === '/users' || path === '/users/login' || path === '/users/xyz') {
    return null;
  }

  if (path === '/users/verify') {
    return '/users/login';
  }

  if (path === '/users/forgot') {
    return '/users/login';
  }

  const picklistEdit = matchPath('/outbound/picklists/edit/:id', path);
  if (picklistEdit) {
    return `/outbound/picklists/${picklistEdit.params.id}`;
  }

  const inboundConfirm = matchPath('/inbound/:type/confirm/:id', path);
  if (inboundConfirm) {
    return `/inbound/${inboundConfirm.params.type}/${inboundConfirm.params.id}`;
  }

  return path.slice(0, path.lastIndexOf('/')) || '/';
};

export const useBackNavigation = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return useCallback((previousLink?: string) => {
    if (Capacitor.getPlatform() !== 'android') {
      if (previousLink) {
        void navigate(previousLink);
      } else {
        void navigate(-1);
      }
      return;
    }

    const parentPath = getParentPath(pathname);
    if (parentPath) {
      void navigate(parentPath, { replace: true });
    } else {
      void CapApp.exitApp();
    }
  }, [navigate, pathname]);
};

export const AndroidBackController = () => {
  const goBack = useBackNavigation();

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    let listenerHandle: PluginListenerHandle | null = null;
    let isDisposed = false;

    const registerBackButtonListener = async () => {
      const handle = await CapApp.addListener('backButton', () => goBack());
      if (isDisposed) {
        await handle.remove();
      } else {
        listenerHandle = handle;
      }
    };

    void registerBackButtonListener();

    return () => {
      isDisposed = true;
      if (listenerHandle) {
        void listenerHandle.remove();
      }
    };
  }, [goBack]);

  return null;
};
